import React, { useRef, useEffect } from "react";
import * as THREE from "three";

/**
 * Hero canvas — "Global Ad Network" visualization (the iconic ad-tech motif).
 *
 *  • A slowly rotating wireframe globe (Earth-like) with city dots that pulse.
 *  • Curved bezier arcs connecting cities — representing bid/impression traffic.
 *  • Light packets travelling along the arcs, looping endlessly.
 *  • Subtle particle dust + halo for depth.
 *
 * This is the canonical "global digital advertising at scale" visual and
 * speaks directly to the ad-tech audience.
 */
export default function HeroCanvas() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0, 7);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // The whole globe + its arcs + dots live in this group so we can rotate
    // them together as one "Earth".
    const earth = new THREE.Group();
    scene.add(earth);

    // ── Wireframe globe sphere ────────────────────────────────────────────
    const R = 1.9;
    const sphereGeo = new THREE.SphereGeometry(R, 48, 32);
    const sphere = new THREE.Mesh(
      sphereGeo,
      new THREE.MeshBasicMaterial({
        color: 0x0047ff,
        wireframe: true,
        transparent: true,
        opacity: 0.18,
      }),
    );
    earth.add(sphere);

    // Solid inner sphere to occlude back-facing arcs (so arcs on the far side
    // are not visible — gives a "this is a real planet" feel).
    const occluder = new THREE.Mesh(
      new THREE.SphereGeometry(R * 0.985, 48, 32),
      new THREE.MeshBasicMaterial({ color: 0x020617 }),
    );
    earth.add(occluder);

    // ── Continents — random dots scattered across the sphere ──────────────
    // (Procedural "dotted earth" — looks like a satellite view at scale.)
    const continentCount = 1400;
    const contPos = new Float32Array(continentCount * 3);
    for (let i = 0; i < continentCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = R * 1.005;
      contPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      contPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      contPos[i * 3 + 2] = r * Math.cos(phi);
    }
    const contGeo = new THREE.BufferGeometry();
    contGeo.setAttribute("position", new THREE.BufferAttribute(contPos, 3));
    const continents = new THREE.Points(
      contGeo,
      new THREE.PointsMaterial({
        size: 0.022,
        color: 0x00f0ff,
        transparent: true,
        opacity: 0.55,
        depthTest: true,
        depthWrite: false,
      }),
    );
    earth.add(continents);

    // ── Major "cities" — bright pulsing markers ───────────────────────────
    // Picked to approximate real ad-tech hubs around the globe.
    const cityCoords = [
      { lat: 40.7, lon: -74.0, name: "NYC" },
      { lat: 51.5, lon: -0.1, name: "LON" },
      { lat: 48.85, lon: 2.35, name: "PAR" },
      { lat: 35.68, lon: 139.69, name: "TYO" },
      { lat: 22.3, lon: 114.17, name: "HKG" },
      { lat: 19.07, lon: 72.87, name: "BOM" },
      { lat: 28.61, lon: 77.2, name: "DEL" },
      { lat: 1.35, lon: 103.82, name: "SGP" },
      { lat: -33.87, lon: 151.21, name: "SYD" },
      { lat: -23.55, lon: -46.63, name: "SAO" },
      { lat: 37.77, lon: -122.42, name: "SFO" },
      { lat: 25.2, lon: 55.27, name: "DXB" },
      { lat: 55.75, lon: 37.62, name: "MOW" },
      { lat: 52.52, lon: 13.4, name: "BER" },
      { lat: -34.6, lon: -58.4, name: "BUE" },
      { lat: 31.23, lon: 121.47, name: "SHA" },
    ];

    const latLonToVec3 = (lat, lon, radius) => {
      const phi = (90 - lat) * (Math.PI / 180);
      const theta = (lon + 180) * (Math.PI / 180);
      return new THREE.Vector3(
        -radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.cos(phi),
        radius * Math.sin(phi) * Math.sin(theta),
      );
    };

    const cities = cityCoords.map((c) => latLonToVec3(c.lat, c.lon, R * 1.01));

    // City dot points
    const cityGeo = new THREE.BufferGeometry().setFromPoints(cities);
    const cityPoints = new THREE.Points(
      cityGeo,
      new THREE.PointsMaterial({
        size: 0.13,
        color: 0x00f0ff,
        transparent: true,
        opacity: 1,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    earth.add(cityPoints);

    // ── Arc tubes between random city pairs ───────────────────────────────
    const arcCount = 14;
    const arcs = [];
    // Each arc holds the cubic bezier control points & a list of "light
    // packets" travelling along it.
    for (let i = 0; i < arcCount; i++) {
      const a = cities[Math.floor(Math.random() * cities.length)];
      let b = a;
      let attempts = 0;
      while (b.equals(a) && attempts < 5) {
        b = cities[Math.floor(Math.random() * cities.length)];
        attempts++;
      }
      // Midpoint lifted above the surface for the arc bulge.
      const mid = a.clone().add(b).multiplyScalar(0.5);
      const dist = a.distanceTo(b);
      const bulge = R + dist * 0.55;
      mid.normalize().multiplyScalar(bulge);

      // Build a Catmull-Rom curve over 3 control points → smooth great-arc
      const curve = new THREE.CatmullRomCurve3([a.clone(), mid, b.clone()]);

      // Draw the arc as a thin line
      const arcPoints = curve.getPoints(40);
      const arcGeo = new THREE.BufferGeometry().setFromPoints(arcPoints);
      const arcLine = new THREE.Line(
        arcGeo,
        new THREE.LineBasicMaterial({
          color: i % 2 === 0 ? 0x00f0ff : 0x8a2be2,
          transparent: true,
          opacity: 0.45,
        }),
      );
      earth.add(arcLine);

      // Packets — small spheres travelling along the curve
      const packets = [];
      const packetCount = 2 + Math.floor(Math.random() * 2);
      for (let j = 0; j < packetCount; j++) {
        const p = new THREE.Mesh(
          new THREE.SphereGeometry(0.04, 12, 8),
          new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.95,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
          }),
        );
        earth.add(p);
        packets.push({
          mesh: p,
          t: Math.random(),
          speed: 0.003 + Math.random() * 0.004,
        });
      }
      arcs.push({ curve, packets });
    }

    // ── Halo behind the globe ─────────────────────────────────────────────
    const haloCanvas = document.createElement("canvas");
    haloCanvas.width = 256;
    haloCanvas.height = 256;
    const ctx = haloCanvas.getContext("2d");
    const grad = ctx.createRadialGradient(128, 128, 50, 128, 128, 128);
    grad.addColorStop(0, "rgba(0,71,255,0.55)");
    grad.addColorStop(0.5, "rgba(0,240,255,0.18)");
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 256, 256);
    const haloTex = new THREE.CanvasTexture(haloCanvas);
    const halo = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: haloTex,
        transparent: true,
        opacity: 0.7,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    halo.scale.set(6.5, 6.5, 1);
    halo.position.z = -0.5;
    scene.add(halo);

    // ── Background star/dust field ────────────────────────────────────────
    const dustCount = 700;
    const dustPos = new Float32Array(dustCount * 3);
    const dustColors = new Float32Array(dustCount * 3);
    const cCobalt = new THREE.Color("#0047FF");
    const cCyan = new THREE.Color("#00F0FF");
    const cWhite = new THREE.Color("#FFFFFF");
    for (let i = 0; i < dustCount; i++) {
      dustPos[i * 3] = (Math.random() - 0.5) * 24;
      dustPos[i * 3 + 1] = (Math.random() - 0.5) * 14;
      dustPos[i * 3 + 2] = (Math.random() - 0.5) * 14 - 4;
      const mix = Math.random();
      const c = mix < 0.5 ? cCyan : mix < 0.85 ? cCobalt : cWhite;
      dustColors[i * 3] = c.r;
      dustColors[i * 3 + 1] = c.g;
      dustColors[i * 3 + 2] = c.b;
    }
    const dustGeo = new THREE.BufferGeometry();
    dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPos, 3));
    dustGeo.setAttribute("color", new THREE.BufferAttribute(dustColors, 3));
    const dust = new THREE.Points(
      dustGeo,
      new THREE.PointsMaterial({
        size: 0.025,
        vertexColors: true,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    scene.add(dust);

    // ── Mouse parallax ───────────────────────────────────────────────────
    const mouse = { x: 0, y: 0 };
    const onMove = (e) => {
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener("mousemove", onMove);

    let raf;
    const animate = (t) => {
      const time = t * 0.001;

      // Earth rotates slowly on Y axis (west → east)
      earth.rotation.y = time * 0.12;
      // Tilt the earth (axial tilt aesthetic)
      earth.rotation.x = -0.25;

      // City dots breathe
      const breathe = 0.7 + 0.3 * (0.5 + 0.5 * Math.sin(time * 2.4));
      cityPoints.material.opacity = breathe;
      cityPoints.material.size = 0.11 + 0.04 * Math.sin(time * 2.4);

      // Move packets along their arcs
      arcs.forEach((arc) => {
        arc.packets.forEach((p) => {
          p.t += p.speed;
          if (p.t > 1) p.t = 0;
          const pos = arc.curve.getPoint(p.t);
          p.mesh.position.copy(pos);
          // Apply same rotation as earth so packet stays "on the globe"
          p.mesh.position.applyEuler(earth.rotation);
          // Twinkle
          p.mesh.material.opacity = 0.6 + 0.4 * Math.sin(time * 6 + p.t * 10);
        });
      });

      // Halo pulse
      halo.material.opacity = 0.55 + 0.15 * Math.sin(time * 1.5);

      // Dust drift
      dust.rotation.y = time * 0.02;

      // Camera parallax
      camera.position.x += (mouse.x * 0.6 - camera.position.x) * 0.04;
      camera.position.y += (mouse.y * 0.4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    const onResize = () => {
      if (!mount) return;
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
      sphereGeo.dispose();
      sphere.material.dispose();
      occluder.geometry.dispose();
      occluder.material.dispose();
      contGeo.dispose();
      continents.material.dispose();
      cityGeo.dispose();
      cityPoints.material.dispose();
      arcs.forEach((a) => {
        a.packets.forEach((p) => {
          p.mesh.geometry.dispose();
          p.mesh.material.dispose();
        });
      });
      haloTex.dispose();
      halo.material.dispose();
      dustGeo.dispose();
      dust.material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      aria-hidden
      className="absolute inset-0 -z-0"
      style={{ pointerEvents: "none" }}
    />
  );
}
